import { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useFilters } from "@/components/FilterBar";
import { QueryPanel } from "@/components/States";
import { Button, Card, Select } from "@/components/ui";
import { useMetaKeys, useTimeseries } from "@/lib/api/queries";
import { defaultInterval } from "@/lib/filters";
import { formatBucket, formatCompact, formatNumber, formatUsd, seriesColor } from "@/lib/format";
import type { IntervalName, MetricName } from "@/types/api";

const METRICS: Array<{ value: MetricName; label: string }> = [
  { value: "tokens", label: "토큰" },
  { value: "cost", label: "비용" },
  { value: "calls", label: "호출수" },
];

const BASE_GROUPS = [
  { value: "", label: "그룹 없음" },
  { value: "service", label: "서비스별" },
  { value: "model", label: "모델별" },
  { value: "provider", label: "프로바이더별" },
  { value: "environment", label: "환경별" },
  { value: "operation", label: "오퍼레이션별" },
  { value: "status", label: "성공/실패" },
];

export function Timeseries() {
  const [filters] = useFilters();
  const [metric, setMetric] = useState<MetricName>("tokens");
  const [groupBy, setGroupBy] = useState<string>("service");
  const [stacked, setStacked] = useState(true);
  const [interval, setInterval] = useState<IntervalName | "auto">("auto");

  const resolvedInterval: IntervalName = interval === "auto" ? defaultInterval(filters) : interval;
  const query = useTimeseries(filters, metric, resolvedInterval, groupBy || null);
  const metaKeys = useMetaKeys(filters);

  // Custom fields are groupable too — `metadata.<key>` goes straight through.
  const groupOptions = [
    ...BASE_GROUPS,
    ...(metaKeys.data?.keys ?? []).map((meta) => ({
      value: `metadata.${meta.key}`,
      label: `${meta.key} (커스텀)`,
    })),
  ];

  const formatValue = (value: number) => (metric === "cost" ? formatUsd(value) : formatNumber(value));

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select
          label="지표"
          size="medium"
          value={metric}
          options={METRICS}
          onChange={(event) => setMetric(event.target.value as MetricName)}
          style={{ width: 140 }}
        />
        <Select
          label="그룹"
          size="medium"
          value={groupBy}
          placeholder="그룹 없음"
          options={groupOptions}
          onChange={(event) => setGroupBy(event.target.value)}
          style={{ width: 200 }}
        />
        <Select
          label="간격"
          size="medium"
          value={interval}
          options={[
            { value: "auto", label: `자동 (${resolvedInterval === "hour" ? "시간" : "일"})` },
            { value: "hour", label: "시간별" },
            { value: "day", label: "일별" },
          ]}
          onChange={(event) => setInterval(event.target.value as IntervalName | "auto")}
          style={{ width: 160 }}
        />
        {groupBy && (
          <Button
            size="medium"
            variant={stacked ? "solid" : "outlined"}
            color={stacked ? "primary" : "assistive"}
            onClick={() => setStacked((value) => !value)}
          >
            {stacked ? "스택" : "라인"}
          </Button>
        )}
      </div>

      <QueryPanel query={query} isEmpty={(data) => data.points.length === 0}>
        {(data) => {
          const rows = data.points.map((point) => ({ bucket: point.bucket, ...point.values }));
          const keys = data.keys.length > 0 ? data.keys : ["(unset)"];
          const showLegend = Boolean(groupBy) && keys.length > 1;

          // An ARRAY, not a fragment: Recharts walks its children with
          // React.Children to find axes/tooltip/legend, and a Fragment hides
          // them from that walk — the chart silently renders with no axes.
          const axes = [
            <CartesianGrid key="grid" stroke="var(--line-normal-alternative)" vertical={false} />,
            <XAxis
              key="x"
              dataKey="bucket"
              tickFormatter={(value: string) => formatBucket(value, resolvedInterval)}
              stroke="var(--label-assistive)"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />,
            <YAxis
              key="y"
              stroke="var(--label-assistive)"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={64}
              tickFormatter={(value: number) => (metric === "cost" ? formatUsd(value) : formatCompact(value))}
            />,
            <Tooltip
              key="tooltip"
              contentStyle={{
                background: "var(--background-elevated-normal)",
                border: "1px solid var(--line-normal-alternative)",
                borderRadius: "var(--radius-sm)",
                fontFamily: "var(--font-sans)",
                fontSize: 12,
              }}
              labelFormatter={(value: string) => formatBucket(value, resolvedInterval)}
              formatter={(value: number, name: string) => [formatValue(value), name]}
            />,
            showLegend ? (
              <Legend key="legend" wrapperStyle={{ fontSize: 12, fontFamily: "var(--font-sans)" }} />
            ) : null,
          ];

          return (
            <ResponsiveContainer width="100%" height={420}>
              {stacked && groupBy ? (
                <AreaChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  {axes}
                  {keys.map((key, index) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stackId="1"
                      stroke={seriesColor(key, index)}
                      fill={seriesColor(key, index)}
                      fillOpacity={0.18}
                      strokeWidth={2}
                    />
                  ))}
                </AreaChart>
              ) : (
                <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  {axes}
                  {keys.map((key, index) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={seriesColor(key, index)}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
          );
        }}
      </QueryPanel>
    </Card>
  );
}
